/* eslint-disable @typescript-eslint/no-explicit-any */
import { ChangeEvent, useState } from 'react'
import DatePicker from 'react-datepicker'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'

import { createLink, updateLink } from '../../services/links'
import { splitDigits } from '../../utils/splitCode'
import { Button } from '../Button'
import { CheckBox } from '../CheckBox'
import CodeInput from './CodeInput'
import { FaCircleCheck } from 'react-icons/fa6'
import { toast } from 'react-toastify'
import { handleError } from '../../helpers/handleError'

interface ShowSectionState {
  code: boolean
  dob: boolean
  sensitiveContent: boolean
}

interface IValue {
  value: { title: string; link: string }
  editData: Links
}

export const LockLink = ({ value, editData }: IValue) => {
  const digits = splitDigits(editData.code)

  const [showSection, setShowSection] = useState<ShowSectionState>({
    code: false,
    dob: false,
    sensitiveContent: false,
  })
  const [description, setDescription] = useState<string>(
    editData.codeLockDescription || '',
  )
  const [descriptionDate, setDescriptionDate] = useState<string>(
    editData.ageLockDescription || '',
  )
  const [errors, setErrors] = useState<string>('')
  const [date, setDate] = useState(null)
  const [dateErrors, setDateErrors] = useState<string>('')
  const [isChecked, setIsChecked] = useState<boolean>(
    editData.sensitive || false,
  )
  const [errorsDigit, setErrorsDigit] = useState<string>('')
  const [code, setCode] = useState({
    digit1: editData.code ? digits.val1 : '',
    digit2: editData.code ? digits.val2 : '',
    digit3: editData.code ? digits.val3 : '',
    digit4: editData.code ? digits.val4 : '',
  })

  const location = useLocation()
  const params = new URLSearchParams(location.search)
  const queryId = params.get('id')
  const { t } = useTranslation()

  const handleChangeSensitiveContent = () => {
    setIsChecked(!isChecked)
  }

  const handleChange = (
    event: ChangeEvent<HTMLInputElement>,
    fieldName: string,
  ) => {
    const { value } = event.target
    setCode((prevCode) => ({
      ...prevCode,
      [fieldName]: value,
    }))
  }

  const toggleSection = (section: keyof ShowSectionState) => {
    setShowSection((prevState) => ({
      ...prevState,
      [section]: !prevState[section],
    }))
  }

  const handleChangeDate = (date: any) => {
    setDate(date)
  }

  const handleLockByCode = () => {
    if (!value.title.trim() || !value.link.trim()) {
      setErrors(t('validation'))
      return
    }
    setErrors('')
    if (
      !code.digit1.trim() ||
      !code.digit2.trim() ||
      !code.digit3.trim() ||
      !code.digit4.trim()
    ) {
      setErrorsDigit(t('digitsValidation'))
      return
    }
    setErrorsDigit('')
    const codeString = `${code.digit1}${code.digit2}${code.digit3}${code.digit4}`
    const data = {
      code: codeString,
      codeLockDescription: description,
      link: value.link,
      title: value.title,
      published: editData.published
    }
    const req = queryId ? updateLink(data, queryId) : createLink(data)
    req.then(() => {
      toast.success(t('linkUpdateSuccess'))
    }).catch((er) => {
      handleError(er)
    })
  }

  const handleLockByDOB = () => {
    if (!value.title.trim() || !value.link.trim()) {
      setErrors(t('validation'))
      return
    }
    setErrors('')
    if (!date) {
      setDateErrors(t('dateValidation'))
      return
    }
    setDateErrors('')
    const data = {
      ageLockDescription: descriptionDate,
      age: date,
      link: value.link,
      title: value.title,
    }
    queryId ? updateLink(data, queryId) : createLink(data)
  }

  const handleLockByCheck = () => {
    if (!value.title.trim() || !value.link.trim()) {
      setErrors(t('validation'))
      return
    }
    setErrors('')
    const data = {
      sensitive: isChecked,
      link: value.link,
      title: value.title,
    }
    queryId ? updateLink(data, queryId) : createLink(data)
  }

  const ageStr = new Date(editData.age).toLocaleString()

  return (
    <div>
      <p className="mt-5 font-semibold">{t('lockLink')}</p>
      <p className="mb-2">{t('visitorsAccess')}</p>
      <p className="mt-2 text-sm text-red-600">{errors}</p>
      <div className="mb-3 flex w-full flex-col shadow-md">
        <div className="flex items-center justify-between border-b border-[#f2f2f2] px-3 py-3 cursor-pointer" onClick={() => toggleSection('code')}>
          <button
            className="font-semibold"
            
            type="button"
          >
            {t('btnCode')}
          </button>
          {editData.code ? <FaCircleCheck color="#407BFF" /> : null}
        </div>
        {showSection.code ? (
          <div className="p-3">
            <p>{t('visitorsFourDigitsAccess')}</p>
            <CodeInput code={code} handleChange={handleChange} />
            <hr />
            <p className="mb-1 mt-3">{t('lockDescription')}</p>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t('description')}
              rows={4}
              className="font-roboto leading-0 mb-7 box-border w-full
              rounded-lg border-0 bg-white p-2 text-base text-black
              placeholder-[#c2c2c2] shadow-md outline-none"
            ></textarea>
            <p className="mt-2 text-sm text-red-600">{errorsDigit}</p>
            <Button
              text={t('save')}
              type="button"
              className="mb-4 w-full text-white"
              onClick={handleLockByCode}
            />
            <hr />
          </div>
        ) : null}
        <div className="flex items-center justify-between border-b border-[#f2f2f2] px-3 py-3 cursor-pointer" onClick={() => toggleSection('dob')}>
          <button
            className="font-semibold"
            type="button"
          >
            {t('dateOfBirth')}
          </button>
          {editData.age ? <FaCircleCheck color="#407BFF" /> : null}
        </div>
        {showSection.dob ? (
          <div className="p-3">
            <p className="mb-5 mt-3 px-3">{t('visitorsByAgeAccess')}</p>
            <p>{t('selectAge')}</p>
            <div className="mb-[30px]">
              <DatePicker
                selected={date}
                onChange={handleChangeDate}
                placeholderText={t('selectAge')}
                showYearDropdown
                dropdownMode="select"
                adjustDateOnChange
              />
            </div>
            <p>{t('lockDescription')}</p>
            <textarea
              value={descriptionDate}
              onChange={(e) => setDescriptionDate(e.target.value)}
              placeholder={t('description')}
              rows={4}
              className="font-roboto leading-0 mb-7 box-border w-full
              rounded-lg border-0 bg-white px-2 text-base text-black
              placeholder-[#c2c2c2] shadow-md outline-none"
            ></textarea>
            <p className="mb-4 text-sm text-red-600">{dateErrors}</p>
            <Button
              text={t('save')}
              type="button"
              className="mb-4 w-full text-white"
              onClick={handleLockByDOB}
            />
            <hr />
          </div>
        ) : null}
        <div className="flex items-center justify-between border-b border-[#f2f2f2] px-3 py-3 cursor-pointer" onClick={() => toggleSection('sensitiveContent')}>
          <button
            className="font-semibold"
            type="button"
          >
            {t('sensitiveContent')}
          </button>
          {editData.sensitive ? <FaCircleCheck color="#407BFF" /> : null}
        </div>

        {showSection.sensitiveContent ? (
          <div className="mb-4 mt-2 flex flex-col gap-2 px-3">
            <div className="flex items-center">
              <CheckBox
                isChecked={isChecked}
                handleChange={handleChangeSensitiveContent}
              />
            </div>
            <p>{t('sensitiveContentDescription')}</p>
            <Button
              text={t('save')}
              type="button"
              className="mb-4 mt-4 w-full text-white"
              onClick={handleLockByCheck}
            />
          </div>
        ) : null}
      </div>
    </div>
  )
}
